import { ReactComponent as CloseSVG } from '@/asset/svg/close.svg'
import { ReactComponent as WarningSVG } from '@/asset/svg/warning.svg'
import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { twMerge } from 'tailwind-merge'
import Button from '../Button'
import Mask from '../Mask'

const Modal: React.FC<PropType> = ({
  visible = false,
  zIndex = 950,
  getContainer = document.body,
  title,
  closable = true,
  icon,
  width,
  lockScroll = true,
  destroyOnClose = false,
  mask = true,
  okText,
  cancelText,
  footer,
  children,
  rootClassName,
  onOk = () => {},
  onCancel = () => {}
}) => {
  const [status, setStatus] = useState<'unready' | 'ready'>('unready')

  useEffect(() => {
    if (status === 'unready' && !visible) {
      return
    }

    setStatus('ready')
  }, [visible])

  if (status === 'unready') {
    return null
  }

  const container = typeof getContainer === 'function' ? getContainer() : getContainer

  const handleOkClick = () => {
    onOk()
  }

  const handleCancelClick = () => {
    onCancel()
  }

  const rootClassNames = twMerge('pointer-events-none inset-0', container ? 'fixed' : 'absolute', rootClassName)

  const okBtn = (
    <Button type="primary" onClick={handleOkClick}>
      {okText || 'OK'}
    </Button>
  )
  const cancelBtn = <Button onClick={handleCancelClick}>{cancelText || 'Cancel'}</Button>

  const modal = (
    <div
      className={rootClassNames}
      style={{
        zIndex: zIndex
      }}
    >
      <div
        className="pointer-events-auto relative top-1/2 z-10 mx-auto max-w-[calc(100vw-16px)] -translate-y-1/2"
        style={{
          width: width ? width : '288px',
          visibility: visible ? 'visible' : 'hidden'
        }}
      >
        <div className="rounded-xl bg-white p-4 text-[#002231]">
          {closable && (
            <div className="flex justify-end">
              <button className="flex-grow-0" onClick={handleCancelClick}>
                <CloseSVG className="h-6 w-6" />
              </button>
            </div>
          )}
          {icon !== false && (
            <div className="mb-4 flex justify-center">
              {icon ? icon : <WarningSVG className="w-16 fill-semantic-warning" />}
            </div>
          )}
          <div className="mb-4">
            {title && <h3 className="mb-2 text-center">{title}</h3>}
            <div className="text-center text-gray-500">{destroyOnClose ? (visible ? children : null) : children}</div>
          </div>
          {footer !== null && (
            <div className="flex flex-col gap-2">
              {okText !== false && okBtn}
              {cancelText !== false && cancelBtn}
            </div>
          )}
        </div>
      </div>

      {mask && (
        <Mask visible={visible} getContainer={false} zIndex={1} lockScroll={lockScroll} onClick={handleCancelClick} />
      )}
    </div>
  )

  return container ? createPortal(<div>{modal}</div>, document.body) : <>{modal}</>
}

interface PropType {
  visible?: boolean
  zIndex?: number
  getContainer?: false | HTMLElement | (() => HTMLElement)
  title?: string
  closable?: boolean
  width?: number | string
  icon?: React.ReactNode
  lockScroll?: boolean
  destroyOnClose?: boolean
  mask?: boolean
  footer?: React.ReactNode | null
  okText?: string | false
  cancelText?: string | false
  rootClassName?: string
  children?: React.ReactNode
  onCancel?: () => void
  onOk?: () => void
}

export default Modal
